/**
 * MODAL: UPLOAD FILE
 * Modale pour uploader un fichier et lui donner un nom
 */

import { useState, useRef } from 'react';
import { Upload, X } from 'lucide-react';
import { Modal, ModalFooter } from '../ui/modal';

interface UploadFileModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (file: File, name: string) => Promise<boolean>;
}

export function UploadFileModal({ isOpen, onClose, onSubmit }: UploadFileModalProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileName, setFileName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      // Pré-remplir le nom avec le nom du fichier (sans extension)
      if (!fileName) {
        const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
        setFileName(nameWithoutExt);
      }
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setFileName('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };

  const handleSubmit = async () => {
    if (!selectedFile || !fileName.trim()) return;

    setIsSubmitting(true);
    try {
      const success = await onSubmit(selectedFile, fileName.trim());
      if (success) {
        handleClose();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  const isValid = selectedFile && fileName.trim().length > 0;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Ajouter un fichier"
      size="md"
      footer={
        <ModalFooter
          onCancel={handleClose}
          onSubmit={handleSubmit}
          cancelLabel="Annuler"
          submitLabel="Ajouter"
          isSubmitting={isSubmitting}
        />
      }
    >
      <div className="flex flex-col gap-4">
        {/* Nom du fichier */}
        <div>
          <label htmlFor="file-name" className="block text-sm font-medium text-zinc-900 mb-1">
            Nom du fichier <span className="text-red-500">*</span>
          </label>
          <input
            id="file-name"
            type="text"
            value={fileName}
            onChange={(e) => setFileName(e.target.value)}
            placeholder="Ex: Contrat signé"
            className="w-full px-3 py-2 border border-zinc-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900"
            disabled={isSubmitting}
          />
        </div>

        {/* Upload de fichier */}
        <div>
          <label className="block text-sm font-medium text-zinc-900 mb-1">
            Fichier <span className="text-red-500">*</span>
          </label>

          {!selectedFile ? (
            <div
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-zinc-300 rounded-md p-8 text-center cursor-pointer hover:border-zinc-400 transition-colors"
            >
              <Upload className="h-8 w-8 text-zinc-400 mx-auto mb-2" />
              <p className="text-sm text-zinc-600 mb-1">Cliquez pour sélectionner un fichier</p>
              <p className="text-xs text-zinc-500">Taille maximale : 50 MB</p>
            </div>
          ) : (
            <div className="border border-zinc-200 rounded-md p-4 flex items-center justify-between">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="flex-shrink-0 w-10 h-10 bg-zinc-100 rounded flex items-center justify-center">
                  <span className="text-xl">📎</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-zinc-900 truncate">{selectedFile.name}</p>
                  <p className="text-xs text-zinc-500">{formatFileSize(selectedFile.size)}</p>
                </div>
              </div>
              <button
                onClick={handleRemoveFile}
                className="flex-shrink-0 p-1 hover:bg-zinc-100 rounded transition-colors"
                type="button"
                disabled={isSubmitting}
              >
                <X className="h-4 w-4 text-zinc-500" />
              </button>
            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileChange}
            className="hidden"
            disabled={isSubmitting}
          />
        </div>

        {/* Message d'aide */}
        {!isValid && (
          <p className="text-xs text-zinc-500">
            Veuillez sélectionner un fichier et lui donner un nom
          </p>
        )}
      </div>
    </Modal>
  );
}
